import { useState, useRef } from 'react';
import { useScrollReveal } from '../../../hooks/useScrollReveal';
import { useTranslation } from 'react-i18next';

interface Agent {
  name: string;
  title: string;
  phone: string;
  email: string;
  photo: string;
}

interface ContactSectionProps {
  agent: Agent;
}

export default function ContactSection({ agent }: ContactSectionProps) {
  const { t } = useTranslation();
  const { ref: refLeft, isVisible: visLeft } = useScrollReveal();
  const { ref: refRight, isVisible: visRight } = useScrollReveal();
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (charCount > 500) return;
    setSubmitting(true);
    const form = e.currentTarget;
    const data = new URLSearchParams();
    const formData = new FormData(form);
    formData.forEach((value, key) => { data.append(key, value.toString()); });
    try {
      await fetch('https://readdy.ai/api/form/d7018tedp0k8rarn86hg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      setSubmitted(true);
    } catch { setSubmitted(true); } finally { setSubmitting(false); }
  };

  return (
    <section id="contact" className="py-32 bg-[#FAFAF7]">
      <div className="max-w-7xl mx-auto px-8">
        <div className="text-center mb-20">
          <div className="flex items-center justify-center gap-4 mb-5">
            <div className="gold-line" />
            <p className="font-sans text-[10px] tracking-[0.35em] uppercase text-[#B59A6A]">{t('contact.label')}</p>
            <div className="gold-line" />
          </div>
          <h2 className="font-serif text-4xl md:text-5xl font-medium text-[#1C1C1C] max-w-2xl mx-auto leading-snug">{t('contact.heading')}</h2>
          <p className="font-sans text-sm text-[#6B6460] mt-5 font-light">{t('contact.sub')}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-16">
          <div ref={refLeft} className={`lg:col-span-3 reveal-left ${visLeft ? 'visible' : ''}`}>
            {submitted ? (
              <div className="flex flex-col items-start justify-center h-full min-h-[360px] py-12">
                <div className="w-12 h-px bg-[#B59A6A] mb-8" />
                <h3 className="font-serif text-3xl text-[#1C1C1C] mb-4">{t('contact.thanks')}</h3>
                <p className="font-sans text-sm text-[#6B6460] font-light leading-loose max-w-sm">{t('contact.thanksText2')}</p>
              </div>
            ) : (
              <form ref={formRef} data-readdy-form id="contact-form-obsidian" onSubmit={handleSubmit} className="space-y-7">
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <label className="block font-sans text-[10px] tracking-[0.2em] uppercase text-[#6B6460] mb-2">{t('contact.firstName')}</label>
                    <input type="text" name="vorname" required placeholder={t('contact.firstNamePh')}
                      className="w-full bg-transparent border-b border-[#E5DDD3] focus:border-[#B59A6A] outline-none py-3 font-sans text-sm text-[#1C1C1C] placeholder-[#BFBAB5] transition-colors duration-300" />
                  </div>
                  <div>
                    <label className="block font-sans text-[10px] tracking-[0.2em] uppercase text-[#6B6460] mb-2">{t('contact.lastName')}</label>
                    <input type="text" name="nachname" required placeholder={t('contact.lastNamePh')}
                      className="w-full bg-transparent border-b border-[#E5DDD3] focus:border-[#B59A6A] outline-none py-3 font-sans text-sm text-[#1C1C1C] placeholder-[#BFBAB5] transition-colors duration-300" />
                  </div>
                </div>
                <div>
                  <label className="block font-sans text-[10px] tracking-[0.2em] uppercase text-[#6B6460] mb-2">{t('contact.email')}</label>
                  <input type="email" name="email" required placeholder={t('contact.emailPh')}
                    className="w-full bg-transparent border-b border-[#E5DDD3] focus:border-[#B59A6A] outline-none py-3 font-sans text-sm text-[#1C1C1C] placeholder-[#BFBAB5] transition-colors duration-300" />
                </div>
                <div>
                  <label className="block font-sans text-[10px] tracking-[0.2em] uppercase text-[#6B6460] mb-2">{t('contact.phoneOpt')}</label>
                  <input type="tel" name="telefon" placeholder={t('contact.phonePh')}
                    className="w-full bg-transparent border-b border-[#E5DDD3] focus:border-[#B59A6A] outline-none py-3 font-sans text-sm text-[#1C1C1C] placeholder-[#BFBAB5] transition-colors duration-300" />
                </div>
                <div>
                  <label className="block font-sans text-[10px] tracking-[0.2em] uppercase text-[#6B6460] mb-2">{t('contact.message')}</label>
                  <textarea name="nachricht" maxLength={500} rows={4} onChange={(e) => setCharCount(e.target.value.length)}
                    placeholder={t('contact.messagePh')}
                    className="w-full bg-transparent border-b border-[#E5DDD3] focus:border-[#B59A6A] outline-none py-3 font-sans text-sm text-[#1C1C1C] placeholder-[#BFBAB5] transition-colors duration-300 resize-none" />
                  <p className={`font-sans text-[10px] mt-1 text-right ${charCount > 450 ? 'text-red-400' : 'text-[#BFBAB5]'}`}>{charCount}/500</p>
                </div>
                <div className="pt-4">
                  <button type="submit" disabled={submitting || charCount > 500} className="gold-btn w-full text-center disabled:opacity-50 cursor-pointer">
                    {submitting ? t('contact.sending') : t('contact.send')}
                  </button>
                </div>
              </form>
            )}
          </div>

          <div ref={refRight} className={`lg:col-span-2 reveal-right ${visRight ? 'visible' : ''} flex flex-col`}>
            <div className="border border-[#E5DDD3] p-10 bg-white">
              <div className="w-full h-64 overflow-hidden mb-8">
                <img src={agent.photo} alt={agent.name} className="w-full h-full object-cover object-top" />
              </div>
              <div className="w-8 h-px bg-[#B59A6A] mb-5" />
              <h3 className="font-serif text-xl font-medium text-[#1C1C1C] mb-1">{agent.name}</h3>
              <p className="font-sans text-[10px] tracking-widest uppercase text-[#B59A6A] mb-8">{agent.title}</p>
              <div className="space-y-4">
                <a href={`tel:${agent.phone}`} className="flex items-center gap-3 group cursor-pointer">
                  <div className="w-7 h-7 flex items-center justify-center"><i className="ri-phone-line text-[#B59A6A] text-sm" /></div>
                  <span className="font-sans text-sm text-[#1C1C1C] group-hover:text-[#B59A6A] transition-colors duration-300">{agent.phone}</span>
                </a>
                <a href={`mailto:${agent.email}`} className="flex items-center gap-3 group cursor-pointer">
                  <div className="w-7 h-7 flex items-center justify-center"><i className="ri-mail-line text-[#B59A6A] text-sm" /></div>
                  <span className="font-sans text-sm text-[#1C1C1C] group-hover:text-[#B59A6A] transition-colors duration-300">{agent.email}</span>
                </a>
              </div>
            </div>
            <div className="mt-8 flex items-start gap-3">
              <i className="ri-shield-check-line text-[#B59A6A] text-sm mt-0.5" />
              <p className="font-sans text-[11px] text-[#6B6460] font-light leading-relaxed">{t('contact.discreteFull')}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-8 mt-24 pt-10 border-t border-[#E5DDD3] flex flex-col md:flex-row items-center justify-between gap-4">
        <p className="font-sans text-[10px] tracking-widest uppercase text-[#BFBAB5]">{t('footer.copyright')}</p>
        <p className="font-sans text-[10px] text-[#BFBAB5]">{t('footer.studio')} <span className="text-[#B59A6A]">Obsidian Properties</span></p>
      </div>
    </section>
  );
}
